import React, { forwardRef, useEffect, useRef, useState } from "react";
import { theme } from "../../../constants/StyledComponents";
import { IconManager } from "../IconManager/IconManager";
import { ReactComponent as ChevronLeftIcon } from "../../../icons/chevronLeft.svg";
import { ReactComponent as ChevronRightIcon } from "../../../icons/chevronRight.svg";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { BodyTyp } from "../../Typography/Typography";
import { capitalize } from "../../../utils/string.utils";
import {
  Day,
  WeekDays,
  MonthsGrid,
  Month,
  Year,
  YearsGrid,
  CalendarBoxContainer,
  Header,
  CalendarInputContainer,
  InputWrapper,
  CalendarBoxContent,
} from "./CalendarInput.styled";
import { InputProps } from "../Input/Input";
import { ReactComponent as CalendarIcon } from "../../../icons/calendar.svg";
import { DateInput } from "./DateInput/DateInput";
import { MONTH_LABEL, YEAR_LABEL } from "../../../constants/Dictionary";
import { useMergedRefs } from "../../../hooks/useMergedRefs.hook";

export enum CalendarMode {
  DAYS = "days",
  MONTHS = "months",
  YEARS = "years",
}

interface CalendarBoxProps {
  onSelectDate: (date: Date) => void;
  value?: Date | null;
  maxDate?: Date;
  minDate?: Date;
  pickerMode: CalendarMode;
  defaultPickerValue?: Date | null;
}

const CalendarBox = ({
  value,
  onSelectDate,
  maxDate,
  minDate,
  pickerMode,
  defaultPickerValue,
}: CalendarBoxProps) => {
  const safeValue = value && !isNaN(value.getTime()) ? value : null;

  const [selectedDate, setSelectedDate] = useState<Date>(
    safeValue ?? defaultPickerValue ?? new Date(),
  );
  const [currentDate, setCurrentDate] = useState<Date>(
    safeValue ?? defaultPickerValue ?? new Date(),
  );
  const [mode, setMode] = useState<CalendarMode>(pickerMode);
  const [yearRange, setYearRange] = useState<{ start: number; end: number }>({
    start: currentDate.getFullYear() - 6,
    end: currentDate.getFullYear() + 5,
  });

  const daysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const startDayOfMonth = (month: number, year: number) => {
    const startDay = new Date(year, month, 1).getDay();
    return startDay === 0 ? 6 : startDay - 1; // Adjust for Monday start
  };

  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const prevYearRange = () => {
    setYearRange({
      start: yearRange.start - 12,
      end: yearRange.end - 12,
    });
  };

  const nextYearRange = () => {
    setYearRange({
      start: yearRange.start + 12,
      end: yearRange.end + 12,
    });
  };

  const handleDateClick = (day: number) => {
    const newYear = currentDate.getFullYear();
    const newMonth = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const newdDay = day.toString().padStart(2, "0");
    const inputDate = `${newYear}-${newMonth}-${newdDay}`;
    const newDate = new Date(inputDate + "T00:00:00.000Z");
    setSelectedDate(newDate);
    onSelectDate(newDate);
  };

  const handleMonthClick = (month: number) => {
    const newDate = new Date(currentDate.getFullYear(), month, 1);
    if (pickerMode === CalendarMode.MONTHS) {
      setSelectedDate(newDate);
      setCurrentDate(newDate);
      onSelectDate(newDate);
    } else {
      setCurrentDate(newDate);
      setMode(CalendarMode.DAYS);
    }
  };

  const handleYearClick = (year: number) => {
    if (pickerMode === CalendarMode.YEARS) {
      const newDate = new Date(year, currentDate.getMonth(), 1);
      setSelectedDate(newDate);
      onSelectDate(newDate);
    } else {
      const newDate = new Date(year, currentDate.getMonth(), 1);
      setCurrentDate(newDate);
      setMode(CalendarMode.MONTHS);
    }
  };

  const renderDays = () => {
    const days = [];
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();

    const daysInCurrentMonth = daysInMonth(month, year);
    const startDay = startDayOfMonth(month, year);

    const isDayDisabled = (day: number) => {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      if (minDate && date < minDate) return true;
      if (maxDate && date > maxDate) return true;
      return false;
    };

    for (let i = 0; i < startDay; i++) {
      days.push(
        <Day key={`empty-${i}`} $isCurrentMonth={false} $isSelected={false} />,
      );
    }

    for (let day = 1; day <= daysInCurrentMonth; day++) {
      const isSelected =
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === month &&
        selectedDate.getFullYear() === year;
      const isDisabled = isDayDisabled(day);
      days.push(
        <Day
          key={day}
          $isCurrentMonth={true}
          $isSelected={isSelected}
          $isDisabled={isDisabled}
          onClick={() => !isDisabled && handleDateClick(day)}
        >
          <BodyTyp
            variant="3"
            fontWeight={FontWeightEnum.MEDIUM}
            color={
              isSelected
                ? theme.colors.white
                : isDisabled
                  ? theme.colors.grey_4
                  : theme.colors.black
            }
          >
            {day}
          </BodyTyp>
        </Day>,
      );
    }

    return (
      <>
        <WeekDays>
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((day) => (
            <BodyTyp variant="3" fontWeight={FontWeightEnum.REGULAR} key={day}>
              {day}
            </BodyTyp>
          ))}
        </WeekDays>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {days}
        </div>
      </>
    );
  };

  const isMonthDisabled = (month: number, year: number) => {
    const monthDate = new Date(year, month, 1);

    const min = minDate
      ? new Date(minDate.getFullYear(), minDate.getMonth(), 1)
      : null;

    const max = maxDate
      ? new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)
      : null;

    if (min && monthDate < min) {
      return true;
    }
    if (max && monthDate > max) {
      return true;
    }

    return false;
  };

  const isYearDisabled = (year: number) => {
    if (minDate && year < minDate.getFullYear()) {
      return true;
    }
    if (maxDate && year > maxDate.getFullYear()) {
      return true;
    }
    return false;
  };

  const renderMonths = () => {
    const currentMonth = capitalize(
      currentDate.toLocaleString("it-IT", { month: "long" }),
    ).slice(0, 3);

    const months = [
      "Gen",
      "Feb",
      "Mar",
      "Apr",
      "Mag",
      "Giu",
      "Lug",
      "Ago",
      "Set",
      "Ott",
      "Nov",
      "Dic",
    ];

    return (
      <MonthsGrid>
        {months.map((month, index) => {
          const disabled = isMonthDisabled(index, currentDate.getFullYear());
          return (
            <Month
              key={month}
              onClick={() => !disabled && handleMonthClick(index)}
              $isCurrentMonth={currentMonth === month}
              $isDisabled={disabled}
            >
              <BodyTyp
                variant="2"
                fontWeight={FontWeightEnum.MEDIUM}
                color={
                  currentMonth === month
                    ? theme.colors.white
                    : theme.colors.black
                }
              >
                {month}
              </BodyTyp>
            </Month>
          );
        })}
      </MonthsGrid>
    );
  };

  const renderYears = () => {
    const years = [];
    const currentYear = currentDate.getFullYear();

    for (let year = yearRange.start; year <= yearRange.end; year++) {
      const disabled = isYearDisabled(year);
      years.push(
        <Year
          key={year}
          onClick={() => !disabled && handleYearClick(year)}
          $isCurrentYear={currentYear === year}
          $isDisabled={disabled}
        >
          <BodyTyp
            variant="2"
            fontWeight={FontWeightEnum.MEDIUM}
            color={
              currentYear === year ? theme.colors.white : theme.colors.black
            }
          >
            {year}
          </BodyTyp>
        </Year>,
      );
    }

    return <YearsGrid>{years}</YearsGrid>;
  };

  const yearRangeRef = useRef(yearRange);
  yearRangeRef.current = yearRange;

  useEffect(() => {
    if (!value || isNaN(value.getTime())) return;
    setSelectedDate(value);
    setCurrentDate(value);
    const year = value.getFullYear();
    const range = yearRangeRef.current;
    if (year < range.start || year > range.end) {
      const offset = Math.floor((year - range.start) / 12) * 12;
      setYearRange({ start: range.start + offset, end: range.end + offset });
    }
  }, [value]);

  return (
    <CalendarBoxContainer>
      <CalendarBoxContent>
        <Header>
          <IconManager
            onClick={mode === CalendarMode.YEARS ? prevYearRange : prevMonth}
            size={IconSizeEnum.MEDIUM}
          >
            <ChevronLeftIcon />
          </IconManager>
          {mode === CalendarMode.DAYS && (
            <>
              <div
                onClick={() => setMode(CalendarMode.MONTHS)}
                className="calendar-choose"
              >
                <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                  {capitalize(
                    currentDate.toLocaleString("it-IT", { month: "long" }),
                  )}
                </BodyTyp>
              </div>
              <div
                onClick={() => setMode(CalendarMode.YEARS)}
                className="calendar-choose"
              >
                <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                  {currentDate.getFullYear()}
                </BodyTyp>
              </div>
            </>
          )}
          {mode === CalendarMode.MONTHS && (
            <>
              {pickerMode === CalendarMode.MONTHS ? (
                <div
                  onClick={() => setMode(CalendarMode.YEARS)}
                  className="calendar-choose"
                >
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                    {currentDate.getFullYear()}
                  </BodyTyp>
                </div>
              ) : (
                <div
                  onClick={() => setMode(CalendarMode.DAYS)}
                  className="calendar-choose"
                >
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                    {MONTH_LABEL}
                  </BodyTyp>
                </div>
              )}
            </>
          )}
          {mode === CalendarMode.YEARS && (
            <>
              {pickerMode === CalendarMode.YEARS ? (
                <div className="calendar-choose">
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                    {YEAR_LABEL}
                  </BodyTyp>
                </div>
              ) : (
                <div
                  onClick={() => setMode(CalendarMode.MONTHS)}
                  className="calendar-choose"
                >
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                    {YEAR_LABEL}
                  </BodyTyp>
                </div>
              )}
            </>
          )}
          <IconManager
            onClick={mode === CalendarMode.YEARS ? nextYearRange : nextMonth}
            size={IconSizeEnum.MEDIUM}
          >
            <ChevronRightIcon />
          </IconManager>
        </Header>
        {mode === CalendarMode.DAYS && renderDays()}
        {mode === CalendarMode.MONTHS && renderMonths()}
        {mode === CalendarMode.YEARS && renderYears()}
      </CalendarBoxContent>
    </CalendarBoxContainer>
  );
};

interface CalendarInputProps extends Omit<InputProps, "onChange" | "value"> {
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  maxDate?: Date;
  minDate?: Date;
  pickerMode?: CalendarMode;
  defaultPickerValue?: Date | null;
}

export const CalendarInput = forwardRef<HTMLDivElement, CalendarInputProps>(
  (
    {
      onChange,
      value,
      maxDate,
      minDate,
      pickerMode = CalendarMode.DAYS,
      defaultPickerValue,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelectDate = (value: Date) => {
      setIsOpen(false);

      if (onChange) {
        onChange(value);
      }
    };

    const toggleOpen = () => {
      if (props.disabled) {
        return;
      }
      setIsOpen(!isOpen);
    };

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const mergedRef = useMergedRefs(wrapperRef, ref);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <CalendarInputContainer ref={mergedRef}>
        <InputWrapper>
          <DateInput
            value={value}
            onChange={(date) => {
              if (onChange) onChange(date);
            }}
            label={props.label}
            hasError={!!props.error}
            disabled={props.disabled}
            pickerMode={pickerMode}
            endIcon={
              <IconManager
                size={IconSizeEnum.SMALL}
                onClick={toggleOpen}
                disabled={props.disabled}
                id={"select-date"}
              >
                <CalendarIcon />
              </IconManager>
            }
            inputSize={props.inputSize}
            borderVariant={props.borderVariant}
          />
        </InputWrapper>
        {isOpen && !props.disabled && (
          <CalendarBox
            onSelectDate={handleSelectDate}
            value={value}
            maxDate={maxDate}
            minDate={minDate}
            pickerMode={pickerMode}
            defaultPickerValue={defaultPickerValue}
          />
        )}
      </CalendarInputContainer>
    );
  },
);
